// Shared user-facing labels for finance display values.
//
// Backend ids (`inv_demo`, `je_demo`) and slugs (`bank_transfer`, `monthly`)
// must never render verbatim — every page below resolves them through these
// helpers plus the existing list endpoints (chart of accounts, invoices,
// bills), with a human fallback when a lookup misses. Lookups tolerate fetch
// failures with an empty map so pages always render (the screenshot harness
// answers `[]` for endpoints without a fixture).
import { useEffect, useRef, useState } from "react";
import {
  type ChartAccount,
  listBills,
  listChartAccounts,
  listInvoices,
} from "./api";

/** `bank_transfer` → `Bank transfer`; already-human text passes through. */
export function humanizeLabel(value: string): string {
  const words = value.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  if (!words) return value;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const FREQUENCY_LABELS: Record<string, string> = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annual: "Semi-annual",
  annual: "Annual",
};

/** `monthly` → `Monthly` (unknown values fall back to `humanizeLabel`). */
export function frequencyLabel(frequency: string): string {
  return FREQUENCY_LABELS[frequency.toLowerCase()] ?? humanizeLabel(frequency);
}

function useLookupMap<T>(
  load: () => Promise<T[]>,
  pick: (row: T) => [string, string],
): Map<string, string> {
  const [map, setMap] = useState<Map<string, string>>(() => new Map());
  // `pick` is an inline arrow at each call site — read it through a ref so
  // the effect below only re-runs when the loader changes.
  const pickRef = useRef(pick);
  pickRef.current = pick;
  useEffect(() => {
    let cancelled = false;
    load()
      .then((rows) => {
        if (cancelled) return;
        const next = new Map<string, string>();
        for (const row of rows) {
          const [key, label] = pickRef.current(row);
          if (!next.has(key)) next.set(key, label);
        }
        setMap(next);
      })
      .catch(() => {
        // Callers render their human fallback when a lookup misses.
      });
    return () => {
      cancelled = true;
    };
  }, [load]);
  return map;
}

/** Chart account id → `code — name` (e.g. `4000 — Services revenue`). */
export function useAccountLabels(): Map<string, string> {
  return useLookupMap(listChartAccounts, (account: ChartAccount) => [
    account.id,
    `${account.code} — ${account.name}`,
  ]);
}

/** Invoice id → invoice number (e.g. `INV-2026-0142`). */
export function useInvoiceNumbers(): Map<string, string> {
  return useLookupMap(listInvoices, (invoice) => [
    invoice.id,
    invoice.invoice_number,
  ]);
}

/** Bill id → bill number (e.g. `BILL-2026-0087`). */
export function useBillNumbers(): Map<string, string> {
  return useLookupMap(listBills, (bill) => [bill.id, bill.bill_number]);
}
